import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  notification,
  DatePicker,
} from 'antd';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import AddWarrantyComponent from './AddWarrantyComponent';
import AddDocumentsCard from './AddDocumentsCard';
import {
  AppStore,
  CarStore,
  ContactStore,
  CurrentUserStore,
  ShopStore,
} from 'stores';
import moment from 'moment';
import { Logger } from 'stores/Classes';
import ContactFormModal from 'components/Contacts/ContactFormModal';
import { isNumber } from 'lodash';
import { PlusCircleOutlined } from '@ant-design/icons';

dayjs.extend(utc);
dayjs.extend(timezone);

const DarkHeaderModal = styled(Modal)`
  & {
    width: 60% !important;

    @media (max-width: 1615px) {
      width: 70% !important;
    }

    @media (max-width: 1366px) {
      width: 80% !important;
    }

    @media (max-width: 1024px) {
      width: 90% !important;
    }

    @media (max-width: 768px) {
      width: 95% !important;
    }
  }

  .ant-modal-content {
    .ant-modal-close {
      .ant-modal-close-x {
        color: #05103a;
      }
    }

    .ant-modal-header {
      .ant-modal-title {
        color: #05103a;
      }
    }

    .ant-modal-body {
      padding: 12px 18px;
    }
  }

  .footer-row {
    margin-top: 21px !important;

    .ant-col {
      display: flex;
      justify-content: flex-end;
    }
  }

  .save-btn {
    margin-left: 8px;
  }
`;

const FloatingMiKmUnit = styled.span`
  position: absolute;
  top: 48%;
  transform: translateY(-50%);
  right: 35px;
  color: #b6b6b6;
`;

const WarrantyForm = styled.div`
  padding: 10px;

  .ant-form {
    .ant-col-12 {
      .ant-form-item {
        margin: 0;
      }
    }

    .ant-form-item-label {
      line-height: 10px;
    }

    .coverage-duration-period {
      margin-top: 4px;
      label {
        visibility: hidden;
      }
    }

    .vendor {
      margin-top: 4px;
    }
  }
`;

const WarrantyEditForm = ({
  onSubmit,
  onCancel,
  initialData,
  assets,
  vendors,
  saving,
  isEditingNewWarranty,
  onClickAddNewContact,
}) => {
  const [form] = Form.useForm();
  const [localSaving, setLocalSaving] = useState(false);
  const warrantyComponentsRef = useRef(null);
  const documentsCardRef = useRef(null);

  const miKmUserSettings =
    CurrentUserStore.user?.settings?.odometer === 'km' ? 'km' : 'mi';

  useEffect(() => {
    setLocalSaving(saving);
  }, [saving]);

  const handleSubmit = async () => {
    try {
      setLocalSaving(true);

      try {
        await form.validateFields();
      } catch {
        notification.error({
          message: 'Error',
          description: 'Please fill in all required fields',
        });
        setLocalSaving(false);
        return;
      }

      const values = form.getFieldsValue();
      const components = warrantyComponentsRef.current.getComponents();
      const validComponents = components.filter(
        (component) =>
          component.name.trim() !== '' || component.description.trim() !== ''
      );

      const documents = documentsCardRef.current.getDocuments();

      onSubmit({
        ...values,
        components: validComponents,
        documents,
        id: initialData?.id || null,
        key: initialData?.id || initialData?.key || new Date().getTime(),
      });
    } catch (error) {
      Logger.error('Error updating warranty', error);
      notification.error({
        message: 'Error',
        description: 'An error occurred while updating the warranty',
      });
    } finally {
      setLocalSaving(false);
    }
  };

  const handleDateChange = (value) => {
    const duration = form.getFieldValue('coverageDuration');
    const period = form.getFieldValue('coverageDurationPeriod');

    if (duration && period && value) {
      const endDateMoment = value.clone().add(duration, period);
      form.setFieldValue('endDate', endDateMoment);
    }
  };

  const handlePeriodChange = (value) => {
    const startDate = form.getFieldValue('startDate');
    const duration = form.getFieldValue('coverageDuration');

    if (startDate && duration) {
      const endDateMoment = startDate.clone().add(duration, value);
      form.setFieldValue('endDate', endDateMoment);
    }
  };

  const handleDurationChange = (value) => {
    const startDate = form.getFieldValue('startDate');

    if (startDate) {
      const period = form.getFieldValue('coverageDurationPeriod');
      const endDateMoment = startDate.clone().add(value, period);
      form.setFieldValue('endDate', endDateMoment);
    }
  };

  const handleMeterChange = (value) => {
    const range = form.getFieldValue('range');
    if (isNumber(value) && isNumber(range)) {
      form.setFieldValue('endMeter', value + range);
    } else {
      form.setFieldValue('endMeter', null);
    }
  };

  const handleRangeChange = (value) => {
    const startMeter = form.getFieldValue('startMeter');
    if (isNumber(startMeter) && isNumber(value)) {
      form.setFieldValue('endMeter', startMeter + value);
    } else {
      form.setFieldValue('endMeter', null);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      assets: initialData?.assets,
      status: initialData?.status || 'active',
      warrantyName: initialData?.warrantyName,
      coverageType: initialData?.coverageType,
      vendor: initialData?.vendor,
      startDate: initialData?.startDate,
      coverageDuration: initialData?.coverageDuration,
      coverageDurationPeriod: initialData?.coverageDurationPeriod || 'years',
      endDate: initialData?.endDate,
      startMeter: initialData?.startMeter,
      range: initialData?.range,
      endMeter: initialData?.endMeter,
    });
  }, [initialData, form]);

  return (
    <WarrantyForm>
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Row gutter={[8, 8]}>
          <Col span={isEditingNewWarranty ? 9 : 6}>
            <Form.Item
              label="Asset"
              name="assets"
              rules={[
                { required: true, message: 'Please select at least one asset' },
              ]}
            >
              <Select
                disabled={!isEditingNewWarranty}
                mode="multiple"
                placeholder="Select assets"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {assets.map((asset) => (
                  <Select.Option key={asset.id} value={asset.id}>
                    {asset.carName ||
                      asset.unitId ||
                      asset.make + ' ' + asset.model + ' ' + asset.year}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {!isEditingNewWarranty && (
            <Col span={3}>
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: 'Please select a status' }]}
              >
                <Select disabled={true} placeholder="Select status" allowClear>
                  <Select.Option value="Expiring Soon">
                    Expiring Soon
                  </Select.Option>
                  <Select.Option value="Active">Active</Select.Option>
                  <Select.Option value="Expired">Expired</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col span={3}>
            <Form.Item
              label="Warranty Name"
              name="warrantyName"
              rules={[{ required: true, message: 'Please name the warranty' }]}
            >
              <Input placeholder="Warranty Name" />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              label="Coverage Type"
              name="coverageType"
              rules={[
                { required: true, message: 'Please select a coverage type' },
              ]}
            >
              <Select placeholder="Select coverage type" allowClear>
                <Select.Option value="standard">Standard</Select.Option>
                <Select.Option value="extended">Extended</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item label="Vendor" name="vendor" className="vendor">
              <Select
                placeholder="Select vendor"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
                }
              >
                {vendors.map((vendor) => (
                  <Select.Option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Button
              type="link"
              onClick={onClickAddNewContact}
              icon={<PlusCircleOutlined />}
              style={{
                padding: 0,
                top: '-20px',
              }}
            >
              Add Vendor
            </Button>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={3}>
            <Form.Item
              label="Start Date"
              name="startDate"
              rules={[{ required: true, message: 'Required' }]}
            >
              <DatePicker
                placeholder="Start Date"
                onChange={(date) => handleDateChange(date)}
              />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              label="Coverage Duration"
              name="coverageDuration"
              rules={[
                { required: true, message: 'Required' },
                { max: 1000, type: 'number', message: 'Max 1000' },
                { min: 0, type: 'number', message: 'Min 0' },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Duration"
                onChange={handleDurationChange}
              />
            </Form.Item>
          </Col>

          <Col span={3}>
            <Form.Item
              className="coverage-duration-period"
              label="&nbsp;"
              name="coverageDurationPeriod"
            >
              <Select onChange={handlePeriodChange}>
                <Select.Option value="months">Months</Select.Option>
                <Select.Option value="years">Years</Select.Option>
              </Select>
            </Form.Item>
          </Col>

          <Col span={3}>
            <Form.Item
              label="End Date"
              name="endDate"
              rules={[
                { required: true, message: 'Required' },
                { type: 'date', message: 'Invalid date' },
              ]}
            >
              <DatePicker disabled={true} placeholder="End Date" />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              label="Start Meter"
              name="startMeter"
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', message: 'Invalid number' },
                { max: 1000000, type: 'number', message: 'Max 1M' },
                { min: 0, type: 'number', message: 'Min 0' },
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject('Invalid number');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Start Meter"
                onChange={handleMeterChange}
              />
            </Form.Item>
            <FloatingMiKmUnit>{miKmUserSettings}</FloatingMiKmUnit>
          </Col>

          <Col span={3}>
            <Form.Item
              label="Range"
              name="range"
              rules={[
                { required: true, message: 'Required' },
                { type: 'number', message: 'Invalid number' },
                {
                  validator: (_, value) => {
                    if (value < 0) {
                      return Promise.reject('Invalid number');
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="Range"
                onChange={handleRangeChange}
              />
            </Form.Item>
            <FloatingMiKmUnit>{miKmUserSettings}</FloatingMiKmUnit>
          </Col>

          <Col span={4}>
            <Form.Item
              label="End Meter"
              name="endMeter"
              rules={[{ required: true, message: 'Required' }]}
            >
              <InputNumber
                disabled={true}
                style={{ width: '100%' }}
                placeholder="End Meter"
              />
            </Form.Item>
            <FloatingMiKmUnit>{miKmUserSettings}</FloatingMiKmUnit>
          </Col>
        </Row>

        <Row gutter={[8, 8]}>
          <Col span={12}>
            <AddWarrantyComponent
              key={`components-${initialData?.id || new Date().getTime()}`}
              ref={warrantyComponentsRef}
              initialComponents={initialData?.components || []}
            />
          </Col>
          <Col span={12}>
            <AddDocumentsCard
              key={`documents-${initialData?.id || new Date().getTime()}`}
              ref={documentsCardRef}
              documents={initialData?.documents || []}
              onFileUploaded={() => {}}
              overrideUploadProps={{
                accept: 'image/*,.pdf',
              }}
            />
          </Col>
        </Row>

        <Col span={24}>
          <Button
            type="primary"
            htmlType="submit"
            loading={localSaving}
            style={{ float: 'right' }}
          >
            Save
          </Button>
          <Button onClick={onCancel} style={{ float: 'right', marginRight: 8 }}>
            Cancel
          </Button>
        </Col>
      </Form>
    </WarrantyForm>
  );
};

WarrantyEditForm.propTypes = {
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  initialData: PropTypes.object,
  assets: PropTypes.array,
  vendors: PropTypes.array,
  saving: PropTypes.bool,
  isEditingNewWarranty: PropTypes.bool,
  onClickAddNewContact: PropTypes.func,
};

const WarrantyDetailsModal = ({
  visible,
  saving,
  isEditingNewWarranty,
  onSubmit,
  onCancel,
  warranty,
}) => {
  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [savingContact, setSavingContact] = useState(false);

  const CONTACT_FORM_MODAL_ID = 'ADD_CONTACT_FORM_MODAL';

  const getAssets = async () => {
    if (!ShopStore.currentShop.id || ShopStore.currentShop.id === -1) {
      setAssets([]);
      return;
    }
    const assets = await CarStore.getShopCarsBasicInfoAndReturn(
      ShopStore.currentShop.id
    );
    setAssets(assets);
  };

  const getVendors = async () => {
    if (!ShopStore.currentShop.id || ShopStore.currentShop.id === -1) {
      setVendors([]);
      return;
    }
    const vendors = await ContactStore.getShopContacts(
      ShopStore.currentShop.id
    );
    setVendors(vendors);
  };

  const onSubmitContactForm = async (data) => {
    try {
      setSavingContact(true);
      await ContactStore.createShopContact({
        ...data,
        shopId: ShopStore.currentShop.id,
      });
      AppStore.addSuccess('Vendor saved successfully');
      await getVendors();
      AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
    } catch (error) {
      AppStore.addError('Error to save vendor: ' + error.message);
    } finally {
      setSavingContact(false);
    }
  };

  useEffect(() => {
    if (visible) {
      getAssets();
      getVendors();
    }
  }, [visible]);

  const handleFormSubmit = (data) => {
    onSubmit(data);
  };

  return (
    <DarkHeaderModal
      open={visible}
      onCancel={onCancel}
      title="Warranty Details"
      footer={null}
    >
      {warranty && (
        <>
          <Row className="content-row" gutter={[16, 16]}>
            <WarrantyEditForm
              assets={assets}
              vendors={vendors}
              isEditingNewWarranty={isEditingNewWarranty}
              initialData={{
                id: warranty.id,
                key: warranty.key,
                status: warranty.status,
                assets: warranty.id_car ? [warranty.id_car] : warranty.assets,
                warrantyName: warranty.name,
                coverageType: warranty.coverage_type,
                vendor: warranty.id_contact,
                startDate: dayjs(warranty.start_date).tz('America/Toronto'),
                coverageDuration: warranty.coverage_duration,
                coverageDurationPeriod: warranty.coverage_duration_unit,
                endDate: dayjs(warranty.end_date).tz('America/Toronto'),
                startMeter: warranty.start_meter,
                range: warranty.range_meter,
                endMeter: warranty.end_meter,
                documents: warranty.documents,
                components: warranty.components,
              }}
              saving={saving}
              onSubmit={handleFormSubmit}
              onCancel={onCancel}
              onClickAddNewContact={() => {
                AppStore.openModals.set(CONTACT_FORM_MODAL_ID, true);
              }}
            />
          </Row>
          <ContactFormModal
            modalId={CONTACT_FORM_MODAL_ID}
            saving={savingContact}
            title="Add Vendor"
            defaultData={{}}
            onCancel={() => {
              AppStore.openModals.set(CONTACT_FORM_MODAL_ID, false);
            }}
            onSubmit={onSubmitContactForm}
          />
        </>
      )}
    </DarkHeaderModal>
  );
};

WarrantyDetailsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  warranty: PropTypes.object,
  saving: PropTypes.bool,
  isEditingNewWarranty: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

WarrantyDetailsModal.defaultProps = {
  saving: false,
  isEditingNewWarranty: false,
};

export default WarrantyDetailsModal;
